import { Tab, TabPanel, Tabs, Tooltip } from '@blueprintjs/core';
import {
  ace_hooks,
  useLoanApplication,
  useLoanRequests,
} from '@axo/shared/data-access/hooks';
import { ApplicationDataCard } from './ApplicationDataCard';
import styles from './ApplicationData.module.css';
import { loan_request, Product } from '@axo/shared/data-access/types';
import { StatusTag } from '../StatusTag';
import {
  ApplicationDataTabType,
  useApplicationDataStore,
} from '../../store/applicationDataStore';
import { useCallback, useId, useMemo } from 'react';
import { ApplicationDataForm } from './ApplicationDataForm/ApplicationDataForm';
import { DebtRegistryCard } from '../DebtRegistryCard';
import { ApplicationConditionalWrapper } from '../ConditionalWrappers/ApplicationConditionalWrapper';
import { MarketCountryCode } from '@axo/shared/types';
import { ExtraApplicationInfoCard } from '../ExtraApplicationInfoCard/ExtraApplicationInfoCard';

const LOAN_REQUEST_DRAFT_ID = 'LOAN_REQUEST_DRAFT_ID';

const sortRequestByDate = (
  a: loan_request.LoanRequest,
  b: loan_request.LoanRequest,
) => {
  return new Date(b.CreatedAt).getTime() - new Date(a.CreatedAt).getTime();
};

interface ApplicationDataProps {
  applicationID: string;
}

export const ApplicationData: React.FC<ApplicationDataProps> = ({
  applicationID,
}) => {
  const TABS_PARENT_ID = useId();
  const loanRequests = useLoanRequests({ loanApplicationID: applicationID });
  const {
    selectedTab,
    selectedLenderID,
    selectRequest,
    selectOriginal,
    createDraft,
    selectAxoVersion,
  } = useApplicationDataStore(applicationID);
  const { data: applicationData } = useLoanApplication(applicationID);

  const isSecuredLending = applicationData?.Product === Product.SecuredLoan;
  const { data: axoVersionData } = ace_hooks.useAxoVersion(
    isSecuredLending ? applicationID : undefined,
  );

  const axoVersionTabID = `axo-version-${applicationID}`;

  const filteredLoanRequests =
    selectedLenderID && loanRequests.data
      ? loanRequests.data.filter(
          (request) => request.LenderID === selectedLenderID,
        )
      : undefined;

  const handleTabChange = useCallback(
    (newTabID: string) => {
      if (newTabID === applicationID) return selectOriginal();
      if (newTabID === axoVersionTabID) return selectAxoVersion();
      if (newTabID === LOAN_REQUEST_DRAFT_ID) return createDraft();
      if (selectedLenderID)
        return selectRequest({
          lenderID: selectedLenderID,
          requestID: newTabID,
        });
    },
    [
      applicationID,
      createDraft,
      selectOriginal,
      selectRequest,
      selectedLenderID,
      axoVersionTabID,
      selectAxoVersion,
    ],
  );

  const selectedTabID = useMemo(() => {
    if (selectedTab.type === ApplicationDataTabType.DRAFT)
      return LOAN_REQUEST_DRAFT_ID;
    if (selectedTab.type === ApplicationDataTabType.AXO_VERSION)
      return axoVersionTabID;
    else if (
      selectedLenderID &&
      selectedTab.type === ApplicationDataTabType.REQUEST
    )
      return selectedTab.requestID;

    return applicationID;
  }, [applicationID, selectedLenderID, selectedTab, axoVersionTabID]);

  return (
    <div className={styles.layout}>
      <Tabs
        id={TABS_PARENT_ID}
        onChange={handleTabChange}
        selectedTabId={selectedTabID}
      >
        <Tab id={applicationID} title="Original" className={styles.tab} />
        {isSecuredLending && axoVersionData && (
          <Tab
            id={axoVersionTabID}
            title="Axo version"
            className={styles.tab}
          />
        )}
        {filteredLoanRequests?.sort(sortRequestByDate).map((request) => (
          <Tab id={request.ID} key={request.ID} className={styles.tab}>
            {request.Status && (
              <Tooltip content="Loan request status" position="top">
                <StatusTag
                  classes={{ tag: styles.tabStatusTag }}
                  status={request.Status}
                >
                  {request.Status}
                </StatusTag>
              </Tooltip>
            )}
          </Tab>
        ))}

        {selectedTab.type === ApplicationDataTabType.DRAFT && (
          <Tab
            id={LOAN_REQUEST_DRAFT_ID}
            title="New loan request"
            className={styles.tab}
          />
        )}
      </Tabs>
      <ApplicationDataForm applicationID={applicationID}>
        <>
          <TabPanel
            selectedTabId={selectedTabID}
            id={selectedTabID}
            parentId={TABS_PARENT_ID}
            className={styles.tabPanel}
            panel={<ApplicationDataCard applicationID={applicationID} />}
          />

          <ApplicationConditionalWrapper
            whitelist={[
              {
                marketCountry: [MarketCountryCode.Norway],
                productType: [Product.SecuredLoan],
              },
            ]}
          >
            <ExtraApplicationInfoCard
              key={`extra-info-${applicationID}`}
              applicationID={applicationID}
            />
          </ApplicationConditionalWrapper>
          <ApplicationConditionalWrapper
            whitelist={[{ marketCountry: [MarketCountryCode.Norway] }]}
          >
            <DebtRegistryCard applicationID={applicationID} />
          </ApplicationConditionalWrapper>
        </>
      </ApplicationDataForm>
    </div>
  );
};
